import { KeyRound, Loader2, UserPlus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button, Field, Input } from '../components/ui/primitives';
import { fetchAuthStatus, login, setupFirstUser } from '../lib/api';

const PASSWORD_MIN = 12;

export const LoginPage = () => {
  const [mode, setMode] = useState<'loading' | 'login' | 'setup'>('loading');
  const [form, setForm] = useState({ name: '', email: '', password: '', confirm: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // The login screen follows the OS theme; the in-app toggle only applies once signed in.
  useEffect(() => {
    const stored = localStorage.getItem('rentx.theme');
    const dark = stored ? stored === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches;
    document.documentElement.classList.toggle('dark', dark);
  }, []);

  // An empty workspace offers to create the first account instead of asking for one.
  useEffect(() => {
    fetchAuthStatus()
      .then((status) => setMode(status.needsSetup ? 'setup' : 'login'))
      .catch(() => setMode('login'));
  }, []);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (mode === 'setup') {
      if (form.password.length < PASSWORD_MIN) {
        setError(`Use at least ${PASSWORD_MIN} characters — length matters more than symbols.`);
        return;
      }
      if (form.password !== form.confirm) {
        setError('The two passwords do not match.');
        return;
      }
    }

    setBusy(true);
    try {
      if (mode === 'setup') {
        await setupFirstUser({ name: form.name.trim(), email: form.email, password: form.password });
      } else {
        await login(form.email, form.password);
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not sign in');
    } finally {
      setBusy(false);
    }
  };

  if (mode === 'loading') {
    return (
      <div className="flex min-h-full items-center justify-center bg-canvas">
        <Loader2 className="h-5 w-5 animate-spin text-faint" />
      </div>
    );
  }

  const isSetup = mode === 'setup';

  return (
    <div className="relative flex min-h-full items-center justify-center overflow-hidden bg-canvas px-4 py-12">
      {/* A faint measured grid behind the card: this is an instrument panel for watching
          agents work, and the backdrop says so without adding a single word. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: 'radial-gradient(rgb(var(--faint) / 0.22) 1px, transparent 1px)',
          backgroundSize: '22px 22px',
          maskImage: 'radial-gradient(ellipse 55% 55% at 50% 42%, black, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 55% 55% at 50% 42%, black, transparent 100%)',
        }}
      />

      <div className="relative w-full max-w-[23rem]">
        <div className="mb-7 flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand font-display text-lg font-bold leading-none text-brand-ink shadow-raise">
            R
          </span>
          <div>
            <h1 className="font-display text-[18px] font-semibold tracking-tight">
              RentX <span className="text-faint">GTM</span>
            </h1>
            <p className="text-[13px] text-muted">
              {isSetup ? 'Create your workspace account' : 'Sign in to your workspace'}
            </p>
          </div>
        </div>

        <form
          onSubmit={submit}
          className="space-y-4 rounded-panel border border-line bg-surface p-5 shadow-raise"
        >
          {isSetup ? (
            <p className="rounded-lg border border-line bg-raised px-3 py-2.5 text-[13px] leading-relaxed text-muted">
              This workspace has no users yet. The account you create now is the administrator — you
              can invite the rest of the team afterwards from Settings.
            </p>
          ) : null}

          {isSetup ? (
            <Field label="Your name" htmlFor="name">
              <Input
                id="name"
                autoFocus
                autoComplete="name"
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                placeholder="Alex Ndoci"
                required
              />
            </Field>
          ) : null}

          <Field label="Email" htmlFor="email">
            <Input
              id="email"
              type="email"
              autoFocus={!isSetup}
              autoComplete="username"
              value={form.email}
              onChange={(event) => setForm({ ...form, email: event.target.value })}
              placeholder="you@company.com"
              required
            />
          </Field>

          <Field
            label="Password"
            htmlFor="password"
            hint={isSetup ? `At least ${PASSWORD_MIN} characters.` : undefined}
          >
            <Input
              id="password"
              type="password"
              autoComplete={isSetup ? 'new-password' : 'current-password'}
              value={form.password}
              onChange={(event) => setForm({ ...form, password: event.target.value })}
              placeholder="••••••••••••"
              required
            />
          </Field>

          {isSetup ? (
            <Field label="Confirm password" htmlFor="confirm">
              <Input
                id="confirm"
                type="password"
                autoComplete="new-password"
                value={form.confirm}
                onChange={(event) => setForm({ ...form, confirm: event.target.value })}
                placeholder="••••••••••••"
                required
              />
            </Field>
          ) : null}

          {error ? (
            <p
              role="alert"
              className="rounded-lg border border-danger/25 bg-danger/[0.07] px-3 py-2 text-[13px] font-medium leading-relaxed text-danger"
            >
              {error}
            </p>
          ) : null}

          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full"
            loading={busy}
            icon={isSetup ? <UserPlus className="h-4 w-4" /> : <KeyRound className="h-4 w-4" />}
          >
            {isSetup ? 'Create account' : 'Sign in'}
          </Button>
        </form>

        <p className="mt-5 text-center text-xs leading-relaxed text-faint">
          {isSetup
            ? 'You can add your team from Settings after signing in.'
            : 'Your leads, outreach, and content in one place.'}
        </p>
      </div>
    </div>
  );
};
